import Depot             from './Depot';
import * as LatLngType   from './LatLng';
import * as RouteType    from './Route';

export type NumRange = [number,number];

export interface Bounds {
    northeast                     : LatLngType.LatLng;
    southwest                     : LatLngType.LatLng;
};

export interface BoardingSeconds {
    ambulatory                      : number;
    wheelchair                      : number;
}

export type DriverSchedule = Record<
    string,                     // The name of the weekday (monday,tuesday,...)
    [number,                    // The start of the range in minutes from start of the day
    number,                     // The end of the range in minutes from start of the day
    number][]                   // The vehicle id assigned to the driver diring this range (or driver_id sometimes)
>;

export interface Agency {
    _id                             : number;
    name                            : string;
    schedule_by_driver_id           : Record<number,DriverSchedule>;  // Map from driver id to the schedule of the driver
    bounds                          : Bounds;
    workminutes                     : NumRange[][];              // One per each weekday (the range is in minutes counted from start of the day)
    holidays                        : Record<string,string>;     // Map from 'yyyymmdd' to the name of the holiday, e.g. '2023/01/16' => 'MLK Day'
    boundaries?                     : LatLngType.LatLng[][];     // an agency can have multiple disjointed areas
    depots                          : Depot[];
    shift_duration_mins             : number;
    break_duration_mins             : number;
    time_zone                       : string;
    startup_ms                      : number;
    current_ms                      : number;
    accommodations                  : string[];
    demographics_profile            : Record<string,string[]>;
    modes_of_transportation         : string[];
    authentication_methods          : string[];
    max_lateness_seconds            : number;                    // If we are late to some point less than this seconds, we allow the route
    gps_accuracy_meters             : number;                    // We compare geo locations upto this accuracy
    dispatcher_advance_seconds      : number;                    // how far in advance to dispatch the pre-scheduled orders (i.e. convert them to "real" orders)
    maneuver_announcement_meters    : number;                    // How many meters ahead to announce a maneuver
    max_routes_to_try               : number;                    // At most how many geo provider to try when scheduling for a new order
    route_prioritization_metrics    : RouteType.Metrics;         // used in selection of the best candidate route out of many, see Server/Bus.ts
    boarding_seconds                : BoardingSeconds;           // How long it takes to board different types of passengers
    phone_number?                   : string;                    // phone number, used in some messaging
    on_demand_enabled               : boolean;
    fixed_route_enabled             : boolean;                   // If true, then the agency supports fixed route
    fixed_route_fares?              : Record<string,number>;     // by passenger category
    external_vehicle_class?         : string;
    max_order_duration_mins?        : number;                    // orders taking longer that this many minutes won't be scheduled
    min_order_duration_mins?        : number;                    // if an order takes less than that, it won't be accepted
    pre_trip_duration_mins?         : number;                    // how many mins is allocated to pre-trip inspections?
    post_trip_duration_mins?        : number;                    // how long post trip inspections takes?
    max_idle_with_passenger_mins?   : number;                    // max time one passenger can wait for another passenger to board.
    max_wait_for_manifest_ms?       : number;                    // max milliseconds to wait for agency manifest
    order_modal_max_advance_mins?   : number;                    // How many minutes ahead the passengers can order
    order_modal_start_ahead_mins?   : number;                    // orders starting fewer than this many mins before ordering won't be allowed
    order_modal_order_types?        : string[];                  // if not null, gives the list of allowed order types in OrderModal. Otherwise API /passenger/order/types gives these types.
    max_regular_hours_day?          : number;                    // For driver payroll reports
    pickup_latlngs_enabled?         : boolean;                   // An easy switch to turn allowed pickup locations on and off.
    dropoff_latlngs_enabled?        : boolean;                   // An easy switch to turn allowed dropoff locations on and off.
    allowed_latlngs?                : LatLngType.NamedLatLng[];  // if not null then gives a list of locatation where users can be picked at or dropped off
    always_show_stop_buttons?       : boolean;                   // if set to `true` then pickup/dropoff/noshow buttons are shown to drivers regardless of how close the drivers are
    next_day_orders_cutoff_hour?    : number;                    // After this hour in agency time zone the next day reservations are disallowed (the day after next OK)
    issue333_paratransit_areas?     : LatLngType.LatLng[][];     // See https://github.com/TecTransit/TecTransit/issues/333
    min_seconds_at_depot?           : number;                    // Between orders, the scheduler will send a driver to depot if at least this many seconds are available for driver to spent in the depot
    vms_are_locked?                 : boolean;                   // See https://github.com/TecTransit/TecTransit/issues/361#issuecomment-1458605203
    skip_dvis                       : boolean;                   // If true, then drivers are not required to do daily vehicle inspections
    skip_ptis                       : boolean;                   // If true, then drivers are not required to do post trip inspections
    hidden_dvi_checkboxes?          : string[];                  // List of DVI indexes to hide on pre-trip/post-trip. See https://github.com/TecTransit/TecTransit/issues/401
    strict_fixed_route_matching?    : boolean;                   // If true, then name of FR route assigned to the bus must exactly match the name of FR route the bus is searched for.
    min_reroute_seconds             : number;                    // server will wait this many seconds between vehicle reroutings
    reroute_seconds_backoff         : number;                    // server will increate the number of seconds to wait between vehicle reroutings with this exponent
    passenger_reverse_geocode_api?  : boolean;                   // If true, then the server will expose passenger api for reverse geocoding
    public_reverse_geocode_api?     : boolean;                   // If true, then the server will expose public api for reverse geocoding. Be VERY CAREFUL enabling this
    fr_trip_mins_between_stops?     : number;                    // The max number of minutes between stops to allow "holdover" passengers on subsequent trips
    issue422_public_key?            : string;                    // See https://github.com/TecTransit/TecTransit/issues/422
    simulate_reported_location?     : boolean;                   // if true then then vehicle is always shown to be were it is supposed to be (i.e. demo mode)
    issue426_mode?                  : boolean;                   // See https://github.com/TecTransit/TecTransit/issues/426
    s3_bucket_name?                 : string;                    // See https://github.com/TecTransit/TecTransit/issues/428
    s3_region?                      : string;                    // See https://github.com/TecTransit/TecTransit/issues/428
    apc_support?                    : boolean;                   // If true, then driver route page has automatic passenger counting integration
    unavailability_is_checkbox?     : boolean;                   // If true, then "Unavailability Reason" in vechile config is a simple checkbox
    map_id?                         : string;                    // If set, then the map id for google maps
    require_contract_id?            : boolean;                   // See https://github.com/TecTransit/TecTransit/issues/479
    issue482_veeroute_manifests?    : boolean;                   // See https://github.com/TecTransit/TecTransit/issues/482
}

export const filterDriverSchedule = (
    scheduleByDriverId : Record<number,DriverSchedule>,
    filter             : (driverId:number,weekday:string,range:[number,number,number]) => ([number,number,number]|undefined)
) : Record<number,DriverSchedule> => {
    return Object.entries(scheduleByDriverId).reduce((acc,[driverId,driverSchedule]) => {
        const newDriverSchedule = Object.entries(driverSchedule).reduce((acc,[weekday,ranges]) => {
            const newRanges = ranges
                .map( r => {
                    return filter(Number(driverId),weekday,r);
                })
                .filter( r => {
                    if( !r )
                        return false;
                    if( r.length!==3 )
                        return false;
                    return true;
                }) as [number,number,number][];
            if( newRanges.length>0 )
                acc[weekday] = newRanges;
            return acc;
        },{} as DriverSchedule);
        if( Object.keys(newDriverSchedule).length>0 )
            acc[driverId] = newDriverSchedule;
        return acc;
    },{} as Record<string,DriverSchedule>);
}
export const findDriverIds = (
    scheduleByDriverId  : Record<number,DriverSchedule>,
    filter             : (driverId:number,weekday:string,range:[number,number,number]) => ([number,number,number]|undefined)
) : number[] => {
    return Object.keys(filterDriverSchedule(scheduleByDriverId,filter)).map(did=>Number(did));
}

export const rehashSchedule = (
    scheduleByDriverId  : Record<number,DriverSchedule>,
    getFirstIndex       : ((driverId:number,vehicleId:number) => number),
    getThirdElement     : ((driverId:number,vehicleId:number) => number),
    overlapHandler?     : ((firstNdx:number,newRange:[number,number,any],existingRange:[number,number,any],overlap:[number,number])=>void)
) : Record<number,DriverSchedule> => {
    return Object.entries(scheduleByDriverId).reduce( (acc,[driverId,driverSchedule]) => {
        return Object.entries(driverSchedule).reduce( (acc,[weekday,ranges]) => {
            return ranges.reduce( (acc,[start,end,vehicleId]) => {
                const firstNdx = getFirstIndex(Number(driverId),vehicleId);
                if( !acc[firstNdx] )
                    acc[firstNdx] = {};
                if( !acc[firstNdx][weekday] )
                    acc[firstNdx][weekday] = [];
                const newRange     = [start,end,getThirdElement(Number(driverId),vehicleId)] as [number,number,number];
                if( overlapHandler ) {
                    const overlapInfo  = acc[firstNdx][weekday].map( existing_range => {
                        return {
                            existing_range,
                            overlap : [Math.max(existing_range[0],newRange[0]),Math.min(existing_range[1],newRange[1])] as [number,number]
                        };
                    }).filter( r => {
                        return r.overlap[0]<r.overlap[1];
                    })[0];
                    if( overlapInfo )
                        overlapHandler(firstNdx,newRange,overlapInfo.existing_range,overlapInfo.overlap);
                }
                acc[firstNdx][weekday].push(newRange);
                return acc;
            },acc);
        },acc);
    },{} as Record<number,DriverSchedule>);
}

export const canonicalizeDriverSchedule = (
    driverSchedule  : DriverSchedule
) => {
    const lowWeekdays = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
    ];
    return lowWeekdays.reduce( (acc,wd) => {
        // TODO:
        // If a particular weekday has overlapping ranges and vehicles,
        // then merge them
        acc[wd] = driverSchedule[wd]?.sort( (range1,range2) => {
            // First sort by vehicle id
            if( range1[2]!==range2[2] )
                return range1[2]-range2[2];
            // Then sort by the start of the range
            if( range1[0]!==range2[0] )
                return range1[0]-range2[0];
            // Finally sort by th end of the range when all other values are equal
            return range1[1]-range2[1];
        });
        return acc;
    },{} as DriverSchedule);
}

export const getBoardingSeconds = ( agency:Agency, order:{ seats:number, wheelchairs:number } ) : number => {
    return ((order.seats||0)*agency.boarding_seconds.ambulatory+(order.wheelchairs||0)*agency.boarding_seconds.wheelchair);
}

export default Agency;
